import { isEqual } from "lodash";
import { useCurrentRawSnapshotsQuery } from "./useRawSnapshotsQuery";
import type { CommercialRaw, FunctionalRaw } from "@/types/generated/ExperienceRawServiceApi";

/**
 * Returns a computed boolean indicating whether a commercial field
 * differs between the current raw snapshot and the published raw snapshot.
 */
export function useHasCommercialFieldChange(field: keyof CommercialRaw) {
  const rawSnapshotsQuery = useCurrentRawSnapshotsQuery();

  return computed(() => {
    const raw = rawSnapshotsQuery.data.value?.rawSnapshot;
    const published = rawSnapshotsQuery.data.value?.publishedRawSnapshot;
    if (!raw || !published) return false;
    return raw.raw?.commercial?.[field] !== published.raw?.commercial?.[field];
  });
}

/**
 * Returns a computed boolean indicating whether a top-level functional field
 * differs between the current raw snapshot and the published raw snapshot.
 * Handles both primitives and arrays (uses order-insensitive comparison for arrays).
 */
export function useHasFunctionalFieldChange(field: keyof FunctionalRaw) {
  const rawSnapshotsQuery = useCurrentRawSnapshotsQuery();

  return computed(() => {
    const raw = rawSnapshotsQuery.data.value?.rawSnapshot;
    const published = rawSnapshotsQuery.data.value?.publishedRawSnapshot;
    if (!raw || !published) return false;

    const rawValue = raw.raw?.functional?.[field];
    const publishedValue = published.raw?.functional?.[field];

    if (Array.isArray(rawValue) && Array.isArray(publishedValue)) {
      if (rawValue.length !== publishedValue.length) return true;
      const sortedRaw = [...rawValue].sort((a, b) => String(a).localeCompare(String(b)));
      const sortedPublished = [...publishedValue].sort((a, b) => String(a).localeCompare(String(b)));
      return !isEqual(sortedRaw, sortedPublished);
    }

    return rawValue !== publishedValue;
  });
}

/**
 * Returns a computed boolean indicating whether a nested functional value
 * differs between the current raw snapshot and the published raw snapshot.
 * Uses an accessor function to extract the value from the functional section.
 * Uses deep equality for objects/arrays.
 */
export function useHasFunctionalNestedFieldChange<T>(accessor: (functional: FunctionalRaw | undefined) => T) {
  const rawSnapshotsQuery = useCurrentRawSnapshotsQuery();

  return computed(() => {
    const raw = rawSnapshotsQuery.data.value?.rawSnapshot;
    const published = rawSnapshotsQuery.data.value?.publishedRawSnapshot;
    if (!raw || !published) return false;

    const rawValue = accessor(raw.raw?.functional);
    const publishedValue = accessor(published.raw?.functional);

    return !isEqual(rawValue, publishedValue);
  });
}

/**
 * Returns a computed boolean indicating whether the title of an operational experience
 * differs between the current raw snapshot and the published raw snapshot.
 */
export function useHasOperationalTitleChange(operationalId: string) {
  const rawSnapshotsQuery = useCurrentRawSnapshotsQuery();

  return computed(() => {
    const raw = rawSnapshotsQuery.data.value?.rawSnapshot;
    const published = rawSnapshotsQuery.data.value?.publishedRawSnapshot;
    if (!raw || !published) return false;

    const currentOp = raw.raw?.commercial?.operational_experiences?.find((op) => op.operational_id === operationalId);
    const publishedOp = published.raw?.commercial?.operational_experiences?.find(
      (op) => op.operational_id === operationalId
    );

    if (!publishedOp) return !!currentOp;

    return (currentOp?.name ?? "") !== (publishedOp?.name ?? "");
  });
}

/**
 * Returns a computed boolean indicating whether any option name within an operational experience
 * differs between the current raw snapshot and the published raw snapshot.
 */
export function useHasOperationalOptionNamesChange(operationalId: string) {
  const rawSnapshotsQuery = useCurrentRawSnapshotsQuery();

  return computed(() => {
    const raw = rawSnapshotsQuery.data.value?.rawSnapshot;
    const published = rawSnapshotsQuery.data.value?.publishedRawSnapshot;
    if (!raw || !published) return false;

    const currentOp = raw.raw?.commercial?.operational_experiences?.find((op) => op.operational_id === operationalId);
    const publishedOp = published.raw?.commercial?.operational_experiences?.find(
      (op) => op.operational_id === operationalId
    );

    if (!publishedOp) return !!currentOp;

    const currentOptions = currentOp?.options ?? [];
    const publishedOptions = publishedOp?.options ?? [];

    if (currentOptions.length !== publishedOptions.length) return true;

    return currentOptions.some((current) => {
      const prev = publishedOptions.find((p) => p.option_id === current.option_id);
      return current.name !== prev?.name;
    });
  });
}

/**
 * Returns a computed boolean indicating whether a custom commercial field
 * (array of objects with `name`) differs between raw and published snapshots.
 * Compares by item names.
 */
export function useHasCustomCommercialFieldChange(field: keyof CommercialRaw) {
  const rawSnapshotsQuery = useCurrentRawSnapshotsQuery();

  return computed(() => {
    const raw = rawSnapshotsQuery.data.value?.rawSnapshot;
    const published = rawSnapshotsQuery.data.value?.publishedRawSnapshot;
    if (!raw || !published) return false;

    const rawItems = (raw.raw?.commercial?.[field] || []) as Array<{ name: string }>;
    const publishedItems = (published.raw?.commercial?.[field] || []) as Array<{ name: string }>;

    const rawNames = rawItems.map((item) => item.name);
    const publishedNames = publishedItems.map((item) => item.name);

    if (rawNames.length !== publishedNames.length) return true;
    return !rawNames.every((name) => publishedNames.includes(name));
  });
}
